import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/formatNumber';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface TablePaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  /** Noun for the total count, e.g. "products" or "rows". */
  itemLabel?: string;
  pageSize?: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** Appended to the left summary, e.g. " · 3,517 units total". */
  summarySuffix?: string;
}

export function TablePagination({
  page,
  totalPages,
  totalItems,
  itemLabel = 'items',
  pageSize,
  onPageChange,
  className,
  summarySuffix,
}: TablePaginationProps) {
  const safeTotalPages = Math.max(1, totalPages);
  const atFirst = page <= 1;
  const atLast = page >= safeTotalPages;

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 p-4 border-t', className)}>
      <p className="text-sm text-muted-foreground">
        Page {formatNumber(page)} of {formatNumber(safeTotalPages)} · {formatNumber(totalItems)} {itemLabel}
        {pageSize != null && pageSize > 0 ? ` · up to ${pageSize} per page` : null}
        {summarySuffix}
      </p>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={atFirst}
          onClick={() => onPageChange(1)}
          aria-label="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={atFirst}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={atLast}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={atLast}
          onClick={() => onPageChange(safeTotalPages)}
          aria-label="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
